import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  PlusIcon, ChevronRightIcon, ChevronDownIcon, CubeTransparentIcon,
  ArrowDownTrayIcon, MagnifyingGlassIcon, LockClosedIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import TopBar from "../../components/layout/TopBar";
import { Card, CardBody, CardHeader } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import Input from "../../components/ui/Input";
import Badge from "../../components/ui/Badge";
import DesignComparisonPanel from "../../components/charts/DesignComparisonPanel";
import NewDesignWizard from "./NewDesignWizard";
import useProjectStore from "../../store/projectStore";
import useAuthStore from "../../store/authStore";
import { projectsApi } from "../../api/projects";
import { bladesApi } from "../../api/blades";
import { libraryApi } from "../../api/assemblies";
import { useLookups } from "../../hooks/useLookups";

const TYPE_COLOR = { rotating: "blue", static: "green" };
const TYPE_LABEL = { rotating: "Rotating", static: "Static" };

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { setActiveProject, setActiveDesign } = useProjectStore();
  const { user } = useAuthStore();
  const { types: machineTypes, labelFor: labelForMachine } = useLookups("machine_type");
  const { types: mecoTypes, labelFor: labelForMeco } = useLookups("meco_type");

  const [project, setProject] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [topologyFilter, setTopologyFilter] = useState("All");
  const [statusFilter, setStatusFilter]     = useState("All");
  const [mecoFilter, setMecoFilter]         = useState([]);  // [] = All

  const toggleMecoFilter = (t) =>
    setMecoFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);

  // MECO overlay
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [mecoDesigns, setMecoDesigns] = useState({});  // id → full design with construction_lines

  // Copy from other project
  const [showCopy, setShowCopy] = useState(false);
  const [copySearch, setCopySearch] = useState("");
  const [copyResults, setCopyResults] = useState([]);
  const [copyTarget, setCopyTarget] = useState(null);  // design to copy
  const [copyName, setCopyName] = useState("");

  // Project information card (inline edit)
  const [infoOpen,    setInfoOpen]    = useState(true);
  const [infoForm,    setInfoForm]    = useState({ name: "", description: "", erp_id: "", machine_type_id: null });
  const [infoSaving,  setInfoSaving]  = useState(false);
  const [infoSaved,   setInfoSaved]   = useState(false);
  const [infoErr,     setInfoErr]     = useState(null);

  const loadProject = useCallback(() => {
    projectsApi.get(projectId).then(({ data }) => {
      setProject(data);
      setActiveProject(data);
      setDesigns(data.blade_designs || []);
      setInfoForm({ name: data.name, description: data.description ?? "", erp_id: data.erp_id ?? "", machine_type_id: data.machine_type_id ?? null });
    });
  }, [projectId, setActiveProject]);

  const saveInfo = async () => {
    if (!infoForm.name.trim()) { setInfoErr("Name cannot be empty"); return; }
    setInfoSaving(true); setInfoErr(null);
    try {
      const { data } = await projectsApi.update(projectId, {
        name: infoForm.name.trim(),
        description: infoForm.description,
        erp_id: infoForm.erp_id || null,
        machine_type_id: infoForm.machine_type_id,
      });
      setProject(data);
      setActiveProject(data);
      setInfoSaved(true);
      setTimeout(() => setInfoSaved(false), 2500);
    } catch (e) {
      setInfoErr(e.response?.data?.error || "Save failed");
    } finally {
      setInfoSaving(false);
    }
  };

  useEffect(() => { loadProject(); }, [loadProject]);

  // Toggle MECO checkbox — fetch full design on first check
  const toggleMeco = async (design) => {
    const id = design.id;
    const next = new Set(checkedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
      if (!mecoDesigns[id]) {
        const { data } = await bladesApi.get(id);
        setMecoDesigns(prev => ({ ...prev, [id]: data }));
      }
    }
    setCheckedIds(next);
  };

  // Copy search
  const handleCopySearch = async (q) => {
    setCopySearch(q);
    const { data } = await bladesApi.search(q, projectId);
    setCopyResults(data);
  };

  const handleCopyConfirm = async () => {
    if (!copyTarget) return;
    try {
      await libraryApi.copyDesign(copyTarget.id, parseInt(projectId), copyName);
      setShowCopy(false);
      setCopyTarget(null);
      loadProject();
    } catch (e) {
      alert("Copy failed: " + (e.response?.data?.error || "unknown"));
    }
  };

  const openDesign = (d) => {
    setActiveDesign(d);
    navigate(`/projects/${projectId}/blade/${d.id}/summary`);
  };

  if (!project) return <div className="p-6 text-gray-400">Loading…</div>;

  return (
    <>
      <TopBar
        title={project.name}
        actions={
          <div className="flex items-center gap-2">
            <Link to={`/projects/${projectId}/layout`}>
              <Button variant="secondary" icon={WrenchScrewdriverIcon}>Layout Tool</Button>
            </Link>
            <Link to={`/projects/${projectId}/assemblies`}>
              <Button variant="secondary" icon={CubeTransparentIcon}>Assembly</Button>
            </Link>
            <Button variant="secondary" icon={ArrowDownTrayIcon}
              onClick={() => { setCopyResults([]); setCopySearch(""); setShowCopy(true); }}>
              Import design
            </Button>
            <Button icon={PlusIcon} onClick={() => setShowCreate(true)}>New Design</Button>
          </div>
        }
      />

      <main className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* ── Project Information card ── */}
        <div className="bg-white rounded-lg border border-gray-200">
          {/* Header / toggle */}
          <button
            onClick={() => setInfoOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3 text-left">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Project Information</span>
              {project.proj_id && (
                <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                  {project.proj_id}
                </span>
              )}
              {project.erp_id && (
                <span className="text-xs text-gray-400 border border-gray-200 px-2 py-0.5 rounded">
                  {project.erp_id}
                </span>
              )}
              {project.machine_type_id && (
                <span className="text-xs font-medium text-bh-600 bg-bh-50 border border-bh-200 px-2 py-0.5 rounded-full">
                  {labelForMachine(project.machine_type_id)}
                </span>
              )}
            </div>
            {infoOpen
              ? <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
              : <ChevronRightIcon className="h-4 w-4 text-gray-400 shrink-0" />}
          </button>

          {/* Expanded content */}
          {infoOpen && (
            <div className="border-t border-gray-100 px-4 pb-4 pt-3">
              <div className="space-y-0">

                {/* Name — editable */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500 shrink-0">Name</span>
                  <input
                    type="text"
                    value={infoForm.name}
                    onChange={e => { setInfoForm(f => ({ ...f, name: e.target.value })); setInfoSaved(false); }}
                    className="text-sm font-medium text-gray-900 text-right border border-transparent
                      hover:border-gray-200 focus:border-bh-400 focus:outline-none rounded px-1.5 py-0.5
                      bg-transparent focus:bg-white w-64 transition-colors"
                  />
                </div>

                {/* Description — editable */}
                <div className="flex items-start justify-between py-2 border-b border-gray-100 gap-4">
                  <span className="text-sm text-gray-500 shrink-0 mt-0.5">Description</span>
                  <textarea
                    value={infoForm.description}
                    onChange={e => { setInfoForm(f => ({ ...f, description: e.target.value })); setInfoSaved(false); }}
                    rows={2}
                    placeholder="Optional project description…"
                    className="text-sm text-gray-900 text-right border border-transparent
                      hover:border-gray-200 focus:border-bh-400 focus:outline-none rounded px-1.5 py-0.5
                      bg-transparent focus:bg-white w-64 resize-none transition-colors"
                  />
                </div>

                {/* ERP ID — editable */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500 shrink-0">ERP ID</span>
                  <input
                    type="text"
                    value={infoForm.erp_id}
                    onChange={e => { setInfoForm(f => ({ ...f, erp_id: e.target.value })); setInfoSaved(false); }}
                    placeholder="e.g. SAP-2024-00123"
                    className="text-sm text-gray-900 text-right border border-transparent
                      hover:border-gray-200 focus:border-bh-400 focus:outline-none rounded px-1.5 py-0.5
                      bg-transparent focus:bg-white w-64 transition-colors placeholder:text-gray-300"
                  />
                </div>

                {/* Read-only fields */}
                {project.proj_id && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Project ID</span>
                    <span className="text-sm font-mono font-medium text-gray-900">{project.proj_id}</span>
                  </div>
                )}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500 shrink-0">Project type</span>
                  {project.owner_id === user?.id ? (
                    <select
                      value={infoForm.machine_type_id ?? ""}
                      onChange={e => { setInfoForm(f => ({ ...f, machine_type_id: parseInt(e.target.value) || null })); setInfoSaved(false); }}
                      className="text-sm font-medium text-gray-900 text-right border border-transparent
                        hover:border-gray-200 focus:border-bh-400 focus:outline-none rounded px-1.5 py-0.5
                        bg-transparent focus:bg-white w-64 transition-colors">
                      <option value="">— Not set —</option>
                      {machineTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                  ) : (
                    <span className="text-sm font-medium text-gray-900">
                      {project.machine_type_id ? labelForMachine(project.machine_type_id) : "—"}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-500">Owner</span>
                  <span className="text-sm font-medium text-gray-900">
                    {project.owner_full_name || project.owner_username}
                  </span>
                </div>
                {user?.is_admin && project.entity && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-500">Entity</span>
                    <span className="text-sm font-medium text-gray-900">{project.entity}</span>
                  </div>
                )}
              </div>

              {/* Save row */}
              {project.owner_id === user?.id && (
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                  <button onClick={saveInfo} disabled={infoSaving}
                    className="text-sm bg-bh-600 text-white rounded-lg px-4 py-1.5 hover:bg-bh-700
                      disabled:opacity-50 transition-colors font-medium">
                    {infoSaving ? "Saving…" : infoSaved ? "Saved ✓" : "Save"}
                  </button>
                  {infoErr && <p className="text-xs text-red-500">{infoErr}</p>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Design list */}
        {designs.length === 0 ? (
          <Card>
            <CardBody className="text-center py-16">
              <p className="text-gray-500 mb-4">No blade designs yet.</p>
              <Button icon={PlusIcon} onClick={() => setShowCreate(true)}>
                Create first design
              </Button>
            </CardBody>
          </Card>
        ) : (() => {
          const filtered = designs
            .filter(d => topologyFilter === "All" || d.topology === topologyFilter)
            .filter(d => statusFilter === "All" || (statusFilter === "released" ? d.is_released : !d.is_released))
            .filter(d => mecoFilter.length === 0 || mecoFilter.includes(d.meco_type_id));
          return (
            <>
              {/* Filter bar */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Edge</span>
                  {[["All", "All"], ["RoundEdge", "Round Edge"], ["DoveTail", "Dove Tail"]].map(([val, label]) => (
                    <button key={val} onClick={() => setTopologyFilter(val)}
                      className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors
                        ${topologyFilter === val
                          ? "bg-bh-600 text-white border-bh-600"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Status</span>
                  {[["All", "All"], ["released", "Released"], ["draft", "Draft"]].map(([val, label]) => (
                    <button key={val} onClick={() => setStatusFilter(val)}
                      className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors
                        ${statusFilter === val
                          ? "bg-bh-600 text-white border-bh-600"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Type</span>
                  <button onClick={() => setMecoFilter([])}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors
                      ${mecoFilter.length === 0
                        ? "bg-bh-600 text-white border-bh-600"
                        : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                    All
                  </button>
                  {mecoTypes.map(t => (
                    <button key={t.id} onClick={() => toggleMecoFilter(t.id)}
                      className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors
                        ${mecoFilter.includes(t.id)
                          ? "bg-bh-600 text-white border-bh-600"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
                {(topologyFilter !== "All" || statusFilter !== "All" || mecoFilter.length > 0) && (
                  <button onClick={() => { setTopologyFilter("All"); setStatusFilter("All"); setMecoFilter([]); }}
                    className="text-xs text-gray-400 hover:text-gray-600 underline">
                    Clear filters
                  </button>
                )}
              </div>

              {/* Design rows */}
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-400">No designs match the current filters.</p>
              ) : (
                <div className="space-y-2">
                  {filtered.map((d) => (
                    <Card key={d.id}
                      className={`transition-colors ${checkedIds.has(d.id)
                        ? "border-bh-400" : "hover:border-bh-200"}`}>
                      <CardBody className="flex items-center gap-4">
                        <input
                          type="checkbox"
                          checked={checkedIds.has(d.id)}
                          onChange={() => toggleMeco(d)}
                          title="Overlay in MECO view"
                          className="h-4 w-4 rounded border-gray-300 text-bh-600 cursor-pointer shrink-0"
                        />
                        <div className="flex-1 cursor-pointer min-w-0" onClick={() => openDesign(d)}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{d.name}</span>
                            {d.hyco_id && (
                              <span className="text-xs font-mono text-gray-400">{d.hyco_id}</span>
                            )}
                            <Badge color="gray">v{d.version}</Badge>
                            <Badge color="blue">{labelForMeco(d.meco_type_id)}</Badge>
                            <Badge color={d.topology === "RoundEdge" ? "blue" : "purple"}>
                              {d.topology === "RoundEdge" ? "Round Edge" : d.topology === "DoveTail" ? "Dove Tail" : d.topology}
                            </Badge>
                            {d.is_released && (
                              <Badge color="yellow">
                                Released
                              </Badge>
                            )}
                            {d.checked_out_by_id && (
                              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border
                                ${d.checked_out_by_id === user?.id
                                  ? "bg-bh-50 text-bh-700 border-bh-200"
                                  : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                                <LockClosedIcon className="h-3 w-3" />
                                {d.checked_out_by_id === user?.id ? "Checked out by you" : `Checked out · ${d.checked_out_by}`}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 flex-wrap">
                            {(d.rpm && d.flow_rate && d.head) && (
                              <span>
                                nq = {(d.rpm * Math.sqrt(d.flow_rate) / Math.pow(d.head, 0.75)).toFixed(1)}
                              </span>
                            )}
                            {d.n_blades && <span>z = {d.n_blades}</span>}
                            {d.updated_by && (
                              <span className="text-gray-400">
                                Edited by <span className="font-medium text-gray-500">{d.updated_by}</span>
                              </span>
                            )}
                            {d.derived_from?.hyco_id && (
                              <span className="text-gray-400">
                                from{" "}
                                <Link
                                  to={`/projects/${d.derived_from.project_id}/blade/${d.derived_from.id}/summary`}
                                  onClick={e => e.stopPropagation()}
                                  className="font-mono text-bh-600 hover:text-bh-700 hover:underline"
                                >
                                  {d.derived_from.hyco_id}
                                </Link>
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <ChevronRightIcon
                            className="h-5 w-5 text-gray-300 cursor-pointer"
                            onClick={() => openDesign(d)} />
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              )}

              {/* Design comparison preview */}
              {checkedIds.size > 0 && (
                <Card>
                  <CardHeader
                    title={`Design comparison — ${checkedIds.size} design${checkedIds.size > 1 ? "s" : ""} selected`}
                  />
                  <CardBody>
                    <DesignComparisonPanel designIds={checkedIds} mecoDesigns={mecoDesigns} />
                  </CardBody>
                </Card>
              )}
            </>
          );
        })()}
      </main>

      {/* ── New design wizard ── */}
      <NewDesignWizard
        open={showCreate}
        onClose={() => setShowCreate(false)}
        projectId={projectId}
      />

      {/* ── Copy / import design modal ── */}
      <Modal open={showCopy} onClose={() => { setShowCopy(false); setCopyTarget(null); }}
        title="Import design from another project">
        <div className="space-y-4">
          {!copyTarget ? (
            <>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-bh-500"
                  placeholder="Search by design name…"
                  value={copySearch}
                  onChange={e => handleCopySearch(e.target.value)}
                />
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {copyResults.length === 0 && copySearch && (
                  <p className="text-gray-400 text-sm text-center py-4">No designs found.</p>
                )}
                {copyResults.length === 0 && !copySearch && (
                  <p className="text-gray-400 text-sm text-center py-4">
                    Type to search across all projects.
                  </p>
                )}
                {copyResults.map(d => (
                  <button key={d.id}
                    onClick={() => { setCopyTarget(d); setCopyName(`${d.name} (copy)`); }}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 flex items-center justify-between gap-2">
                    <div>
                      <span className="text-sm text-gray-900 font-medium">{d.name}</span>
                      <span className="text-xs text-gray-400 ml-2">v{d.version}</span>
                      {d.is_released && (
                        <span className="text-xs text-yellow-600 ml-2">• Released</span>
                      )}
                      <span className="text-xs text-gray-400 ml-2">
                        — {d.project_name} · {d.owner_full_name || d.owner_username}
                      </span>
                    </div>
                    <Badge color={TYPE_COLOR[d.blade_type] || "gray"} className="shrink-0">
                      {TYPE_LABEL[d.blade_type] || d.blade_type}
                    </Badge>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Copying <span className="font-medium text-gray-900">{copyTarget.name}</span> into this project.
              </p>
              <Input label="Name in this project" value={copyName}
                onChange={e => setCopyName(e.target.value)} />
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={() => setCopyTarget(null)}>Back</Button>
                <Button onClick={handleCopyConfirm} disabled={!copyName}>Copy here</Button>
              </div>
            </>
          )}
        </div>
      </Modal>


    </>
  );
}
